import type { Key } from "react";
import axiosInstance from "../api/axiosInstance";

export interface Member {
  id: number;
  uuid?: string;
  email: string;
  name: string;
  role: "admin" | "member";
  practice_area?: number; // backend numeric value
  practice_area_code?: string | null;
  practice_area_label?: string | null;
  specialty_code?: string | null;
  specialty_label?: string | null;
}

export interface Invitation {
  id: number;
  email: string;
  sent_at: string;
  inviter_name: string;
}

export interface PaginatedResponse<T> {
  count: number;
  results: T[];
  billing_count?: number;
}

export interface InviteResponse {
  invitations: Invitation[];
  has_errors: boolean;
  existing_invites?: string[];
  existing_members?: string[];
  added_members?: string[];
  invalid_emails?: string[];
}

export type OnboardingCatalog = {
  practice_areas: { code: string; label: string }[];
  specialties_by_area: Record<string, { code: string; label: string }[]>;
};

export interface ClinicianClient {
  uuid: string;
  name: string;
  age: number | null;
  last_note_created_at: string | null;
  total_notes: number;
}

export interface ClinicianNote {
  id: number;
  title: string | null;
  client_name: string | null;
  client_uuid: string | null;
  created_at: string;
  note_type: number;
  note_type_label: string;
  status: number;
  is_read: boolean;
}

export interface ClinicianProfile {
  uuid: string;
  name: string;
  email: string;
  role: string;
  practice_area: number | null;
  practice_area_code: string | null;
  practice_area_label: string | null;
  specialty_code: string | null;
  specialty_label: string | null;
}

export interface ClinicianProfileData {
  clinician: ClinicianProfile;
  clients: {
    count: number;
    page: number;
    page_size: number;
    results: ClinicianClient[];
  };
  notes: {
    count: number;
    page: number;
    page_size: number;
    results: ClinicianNote[];
  };
}

export interface GroupBillingInfo {
  has_subscription: boolean;
  payment_method: "credit_card" | "invoice" | null;
  subscription_status?: string;
  current_period_end?: number;
  seats: number;
  active_seats: number;
  minimum_seats: number | null;
  free_seats: number;
  billable_seats: number;
  extra_seats: number;
  is_deposit_active: boolean;
  deposit_amount: string | null;
  price_per_seat: string | null;
  price_per_extra_seat: string | null;
  support_fee: string | null;
  receptionist_price: string | null;
  billing_interval?: string | null;
  recurring_total?: string | null;
  billing_description?: string | null;
}

export const membersService = {
  getMembers: async (page = 1, search?: string, pageSize?: number): Promise<PaginatedResponse<Member>> => {
    const response = await axiosInstance.get<PaginatedResponse<Member>>("/users/group/members/", {
      params: {
        page,
        search: search || undefined, // only include if search has a value
        page_size: pageSize || undefined, // only include if pageSize has a value (max 100)
      },
    });
    return response.data;
  },

  getOnboardingCatalog: async (): Promise<OnboardingCatalog> => {
    const response = await axiosInstance.get<OnboardingCatalog>("/users/catalog/onboarding/");
    return response.data;
  },

  getInvitations: async (page = 1): Promise<PaginatedResponse<Invitation>> => {
    const response = await axiosInstance.get<PaginatedResponse<Invitation>>("/users/group/invitations/", {
      params: { page },
    });
    return response.data;
  },

  inviteMembers: async (emails: string[]): Promise<InviteResponse> => {
    const response = await axiosInstance.post<InviteResponse>("/users/group/invite/", { emails });
    return response.data;
  },

  resendInvitation: async (invitationId: number): Promise<void> => {
    await axiosInstance.post(`/users/group/invitations/${invitationId}/`);
  },

  revokeInvitation: async (invitationId: number): Promise<void> => {
    await axiosInstance.delete(`/users/group/invitations/${invitationId}/`);
  },

  removeMember: async (memberId: number): Promise<void> => {
    await axiosInstance.delete(`/users/group/members/${memberId}/`);
  },

  removeMembersBatch: async (memberIds: Key[]): Promise<void> => {
    await Promise.all(memberIds.map((memberId) => axiosInstance.delete(`/users/group/members/${memberId}/`)));
  },

  revokeInvitationsBatch: async (invitationIds: Key[]): Promise<void> => {
    await Promise.all(
      invitationIds.map((invitationId) => axiosInstance.delete(`/users/group/invitations/${invitationId}/`)),
    );
  },

  promoteToAdmin: async (memberId: number): Promise<void> => {
    await axiosInstance.post(`/users/group/members/${memberId}/promote/`);
  },

  updateMemberPracticeAndSpecialty: async (
    memberId: number,
    payload: { practice_area?: string; specialty_code?: string },
  ): Promise<Member> => {
    const response = await axiosInstance.patch<Member>(`/users/group/members/${memberId}/`, payload);
    return response.data;
  },

  getGroupInviteLink: async (): Promise<{ invite_link: string }> => {
    const response = await axiosInstance.get<{ invite_link: string }>("/users/group/invite-link/");
    return response.data;
  },

  generateGroupPaymentLink: async (billingPeriod?: "monthly" | "yearly"): Promise<{ payment_link: string }> => {
    const response = await axiosInstance.post<{ payment_link: string }>("/users/group/payment-link/", {
      billing_period: billingPeriod,
    });
    return response.data;
  },

  getGroupBilling: async (): Promise<GroupBillingInfo> => {
    const response = await axiosInstance.get<GroupBillingInfo>("/users/group/billing/");
    return response.data;
  },

  openBillingPortal: async (intent?: "pay"): Promise<{ portal_url: string }> => {
    const response = await axiosInstance.post<{ portal_url: string }>(
      "/users/billing-portal/",
      intent ? { intent } : {},
    );
    return response.data;
  },

  getClinicianProfile: async (
    userUuid: string,
    clientsPage = 1,
    notesPage = 1,
    clientUuid?: string,
  ): Promise<ClinicianProfileData> => {
    const response = await axiosInstance.get<ClinicianProfileData>(`/users/clinician/${userUuid}/profile/`, {
      params: {
        clients_page: clientsPage,
        notes_page: notesPage,
        client_uuid: clientUuid || undefined,
      },
    });
    return response.data;
  },
};
